import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Account } from '../models/account.model';
import { CollectLocation } from '../models/location.model';
import { Container } from '../models/container.model';
import { WasteType } from '../models/waste_type.model';
import { Collector } from '../models/collector.model';
import { Collect, CollectInput } from '../models/collects.model';
import { SupportRequestInput } from '../models/support_request.model';
import { map } from 'rxjs/operators';
import { NetworkService } from './network.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Vendor } from '../models/vendor.model';

export const LOCAL_STORAGE_JWT_KEY = 'recycle_tracker_jwt'

const API_BASE = '/api';

@Injectable({
  providedIn: 'root'
})

export class ApiService {
  private isOnline: boolean = true;
  private isOnlineSubscription: Subscription = null;

  constructor(private http: HttpClient, private networkService: NetworkService) {
    this.isOnlineSubscription = this.networkService.isOnline$.subscribe((isOnline) => this.isOnline = isOnline);
  }

  ngOnDestroy() {
    this.isOnlineSubscription?.unsubscribe();
    this.isOnlineSubscription = null;
  }

  public authenticate(body: { jwt: string, email: string }) {
    return this.http.post<Account>(`${API_BASE}/auth/authenticate`, body, this.httpOptions);
  }

  public logout() {
    const post = this.http.post(`${API_BASE}/auth/logout`, {}, this.httpOptions);
    post.subscribe(() => localStorage.removeItem(LOCAL_STORAGE_JWT_KEY));
    return post;
  }

  public currentAccount() {
    const uri = `${API_BASE}/auth/current_account`
    return this.http.get<Account>(uri, this.httpOptions);
  }

  public getLocations() {
    const uri = `${API_BASE}/locations`;

    if (this.isOnline) {
      return this.http.get<CollectLocation[]>(uri, this.httpOptions).pipe(
        map(this.cacheGetRequest(uri)),
      );
    } else {
      const result = this.getCachedRequest<CollectLocation[]>(uri);
      console.log("Within getLocations", result);
      return new BehaviorSubject(result).asObservable();
    }
  }

  public getWasteTypes() {
    const uri = `${API_BASE}/waste_types`;

    if (this.isOnline) {
        return this.http.get<WasteType[]>(uri, this.httpOptions).pipe(
        map(this.cacheGetRequest(uri)),
      );
    } else {
      const result = this.getCachedRequest<WasteType[]>(uri);
      return new BehaviorSubject(result).asObservable();
    }
  }

  public getCollectors() {
    const uri = `${API_BASE}/collectors`;

    if (this.isOnline) {
      return this.http.get<Collector[]>(uri, this.httpOptions).pipe(
        map(this.cacheGetRequest(uri)),
      );
    } else {
      const result = this.getCachedRequest<Collector[]>(uri);
      console.log("Within getCollectors", result);
      return new BehaviorSubject(result).asObservable();
    }
  }

  public getVendors() {
    const uri = `${API_BASE}/vendors`;

    if (this.isOnline) {
      return this.http.get<Vendor[]>(uri, this.httpOptions).pipe(
        map(this.cacheGetRequest(uri)),
      );
    } else {
      const result = this.getCachedRequest<Vendor[]>(uri);
      console.log("Within getVendors", result);
      return new BehaviorSubject(result).asObservable();
    }
  }

  public getContainers() {
    const uri = `${API_BASE}/containers`;

    if (this.isOnline) {
      return this.http.get<Container[]>(uri, this.httpOptions).pipe(
        map(this.cacheGetRequest(uri)),
      );
    } else {
      const result = this.getCachedRequest<Container[]>(uri);
      console.log("Within getContainers", result);
      return new BehaviorSubject(result).asObservable();
    }
  }

  public recordCollect(collect: CollectInput) {
    const data = {
      collect: collect,
    }
    return this.http.post<Collect>(`${API_BASE}/collects`, data, this.httpOptions);
  }

  public patchCollect(collectId: number, collect: CollectInput) {
    const data = {
      collect: collect,
    }
    return this.http.patch<Collect>(`${API_BASE}/collects/${collectId}`, data, this.httpOptions);
  }

  public deleteCollect(collectId: number) {
    return this.http.delete<Collect>(`${API_BASE}/collects/${collectId}`, this.httpOptions);
  }

  public submitSupportRequest(supportRequest: SupportRequestInput) {
    const data = {
      support_request: supportRequest,
    }
    return this.http.post(`${API_BASE}/support_requests`, data, this.httpOptions);
  }

  private get headers() {
    let headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');

    const jwt = localStorage.getItem(LOCAL_STORAGE_JWT_KEY);

    if (jwt) {
      headers = headers.append('X-RTRACKER-JWT', jwt);
    }

    return headers;
  }

  private get httpOptions() {
    return {
      headers: this.headers
    }
  };

  private cacheGetRequest<T>(uri: string) {
    return (apiResponse: T) => {
      const cacheValue = JSON.stringify(apiResponse);
      const cacheKey = this.getCacheKey(uri);
      localStorage.setItem(cacheKey, cacheValue);
      console.log("Logged API Response", apiResponse);
      return apiResponse;
    }
  }

  private getCachedRequest<T>(uri: string) {
    const cacheKey = this.getCacheKey(uri);
    const cachedValue = localStorage.getItem(cacheKey);
    console.log("getCachedRequest", uri);
    return JSON.parse(cachedValue) as T;
  }

  private getCacheKey(uri: string) {
    console.log("getCacheKey", uri);
    return `api:${uri}`;
  }

}
